"use client";

import { useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";

type OutputFormat = "lines" | "comma";

const URL_REGEX = /(?:https?:\/\/|www\.)[^\s<>"']+/g;
const SENTENCE_PUNCTUATION = new Set([".", ",", ";", ":", "'", '"']);
const CLOSING_BRACKETS: Record<string, string> = { ")": "(", "]": "[" };

function countChar(value: string, char: string): number {
  let total = 0;
  for (const each of value) {
    if (each === char) {
      total += 1;
    }
  }
  return total;
}

/**
 * Cut the punctuation a sentence hangs on the end of a link. A closing bracket goes only
 * when it has no opening partner inside the link, so "(see https://x.org/a)" loses its
 * ")" but "https://en.wikipedia.org/wiki/Citation_(style)" keeps its own.
 */
export function trimTrailingPunctuation(url: string): string {
  let end = url.length;
  while (end > 0) {
    const last = url[end - 1] ?? "";
    const opener = CLOSING_BRACKETS[last];
    if (SENTENCE_PUNCTUATION.has(last)) {
      end -= 1;
      continue;
    }
    const body = url.slice(0, end);
    if (opener && countChar(body, last) > countChar(body, opener)) {
      end -= 1;
      continue;
    }
    break;
  }
  return url.slice(0, end);
}

function toDomain(url: string): string {
  const withProtocol = url.startsWith("www.") ? `https://${url}` : url;
  try {
    return new URL(withProtocol).hostname;
  } catch {
    return url;
  }
}

export function extractUrls(text: string, domainOnly: boolean): string[] {
  const matches = text.match(URL_REGEX) ?? [];
  const seen = new Set<string>();
  const urls: string[] = [];
  for (const match of matches) {
    const cleaned = trimTrailingPunctuation(match);
    const value = domainOnly ? toDomain(cleaned) : cleaned;
    const key = value.toLowerCase();
    if (value.length > 0 && !seen.has(key)) {
      seen.add(key);
      urls.push(value);
    }
  }
  return urls;
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const EXTRACT_URLS_STRINGS = {
  onePerLine: "One per line",
  commaSeparated: "Comma-separated",
  options: "Options",
  domainsOnly: "Domains only",
  textToScan: "Text to scan",
  inputPlaceholder:
    "See https://example.com/guide and www.example.org for more.",
  extractedDomains: "Extracted domains",
  extractedUrls: "Extracted URLs",
  domainsFound: "Domains found",
  urlsFound: "URLs found",
  outputFormat: "Output format",
  copied: "Copied!",
  copyResult: "Copy Result",
  clear: "Clear",
} as const;

export default function ExtractUrlsWidget() {
  const [input, setInput] = useState("");
  const [domainOnly, setDomainOnly] = useState(false);
  const [format, setFormat] = useState<OutputFormat>("lines");
  const [copied, setCopied] = useState(false);

  const strings = useToolUi(EXTRACT_URLS_STRINGS);

  const fmt = useToolNumber();

  const outputFormats: { value: OutputFormat; label: string }[] = [
    { value: "lines", label: strings.onePerLine },
    { value: "comma", label: strings.commaSeparated },
  ];

  const urls = extractUrls(input, domainOnly);
  const output = format === "lines" ? urls.join("\n") : urls.join(", ");

  function handleCopy() {
    if (output.length === 0) {
      return;
    }
    writeToClipboard(output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setCopied(false);
  }

  return (
    <>
      {/* Options */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">{strings.options}</p>
        <div className="flex flex-wrap gap-3">
          {outputFormats.map((f) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={f.value}
            >
              <input
                checked={format === f.value}
                className="accent-primary"
                name="output-format"
                onChange={() => setFormat(f.value)}
                type="radio"
                value={f.value}
              />
              {f.label}
            </label>
          ))}
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={domainOnly}
              className="accent-primary"
              onChange={(e) => setDomainOnly(e.target.checked)}
              type="checkbox"
            />
            {strings.domainsOnly}
          </label>
        </div>
      </section>

      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="urls-input"
        >
          {strings.textToScan}
        </label>
        <textarea
          className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="urls-input"
          onChange={(e) => {
            setInput(e.target.value);
            setCopied(false);
          }}
          placeholder={strings.inputPlaceholder}
          value={input}
        />
      </div>

      {/* Output */}
      {output.length > 0 && (
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="urls-output"
          >
            {domainOnly ? strings.extractedDomains : strings.extractedUrls}
          </label>
          <textarea
            className="min-h-[140px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
            id="urls-output"
            readOnly
            value={output}
          />
        </div>
      )}

      {/* Stats */}
      {input.trim().length > 0 && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-2">
          <StatCard
            label={domainOnly ? strings.domainsFound : strings.urlsFound}
            value={fmt(urls.length)}
          />
          <StatCard
            label={strings.outputFormat}
            value={
              format === "lines" ? strings.onePerLine : strings.commaSeparated
            }
          />
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={output.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyResult}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
    </>
  );
}
